import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { type PersonaFormState } from '..';

import type {
  PermissionMap,
  Persona,
  PersonaRowData,
  PersonaToast,
  UserSettings,
} from 'admin/types';
import { useAppSelector } from 'redux-stores';
import type { PersonaCreateFields, PersonaUpdateFields } from 'js-sdk/src/types/Persona';
import { type ScimGroup } from 'js-sdk/src/services/Admin/utils/types';

export type PersonaFormErrors = {
  name?: string[];
  description?: string[];
};

export type PersonaScimFormErrors = PersonaFormErrors & {
  scimGroups?: string[];
};

export type PersonaTitleFormErrors = PersonaFormErrors & {
  titles?: string[];
};

export type PersonaBaseFormPayload = {
  name?: string;
  description?: string | null;
};

export type BaseStateProps = {
  selectedPersona?: PersonaRowData;
  setToastMessageData: React.Dispatch<React.SetStateAction<PersonaToast | undefined>>;
  setPersonaFormState: (personaFormState: PersonaFormState) => void;
  editMode: boolean;
};

export type CommonFormProps = {
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setPersonaFormState: React.Dispatch<React.SetStateAction<PersonaFormState>>;
  setSelectedPersona: (persona: PersonaRowData | undefined) => void;
};

export type PersonaTitleFormPayload = PersonaBaseFormPayload & {
  titles?: string[];
};

export type PersonaScimFormPayload = PersonaBaseFormPayload & {
  scimGroups?: string[];
};

export const baseValidationSchema = z.object({
  name: z
    .string({ required_error: 'Persona Name is required.' })
    .min(1, 'Persona Name is required.'),
  description: z.string().optional(),
});

export const getFormErrors = <T extends 'title' | 'scim'>(
  persona: T extends 'title' ? PersonaTitleFormPayload : PersonaScimFormPayload,
  schema: z.ZodSchema
): T extends 'title' ? PersonaTitleFormErrors : PersonaScimFormErrors => {
  const result = schema.safeParse(persona);
  if (!result.success) {
    const errors = result.error.flatten().fieldErrors;
    return errors;
  }
  return {} as T extends 'title' ? PersonaTitleFormErrors : PersonaScimFormErrors;
};

export type MobxProps = {
  isPersonaMessageTtlAndPinLockEnabled: boolean;
  loadTitles: (selectedOrganizationId: string, personaId: string) => Promise<string[]>;
  createPersona: (
    payload: PersonaCreateFields,
    selectedOrganizationId: string
  ) => Promise<Persona[]>;
  updatePersona: (
    payload: PersonaUpdateFields,
    personaId: string,
    selectedOrganizationId: string
  ) => Promise<Persona>;
  findSCIMGroups: (
    selectedOrganizationId: string,
    query?: string
  ) => Promise<{
    metadata: {
      firstHit: number;
      totalHits: number;
      itemsPerPage: number;
    };
    results: ScimGroup[];
  }>;
};

const useCommonState = <T extends 'title' | 'scim'>({
  selectedPersona,
  setToastMessageData,
  isPersonaMessageTtlAndPinLockEnabled,
}: Pick<BaseStateProps, 'selectedPersona' | 'setToastMessageData'> &
  Pick<MobxProps, 'isPersonaMessageTtlAndPinLockEnabled'>) => {
  const [loading, setLoading] = useState(false);
  const [commonFormFields, setCommonFormFields] = useState<PersonaBaseFormPayload>({
    name: selectedPersona?.name,
    description: selectedPersona?.description,
  });
  const [template, setTemplate] = useState<PersonaRowData>();
  const [canRevert, setCanRevert] = useState(false);
  const selectedOrganizationId = useAppSelector((state) => state.admin.selectedOrganizationId);
  const orgSettings = useAppSelector((state) => state.orgSettings.settings);

  const [userPermissions, setUserPermissions] = useState<PermissionMap | undefined>({});
  const [userSettings, setUserSettings] = useState<UserSettings>();

  const defaultUserSettings = useMemo(() => {
    return {
      ...(isPersonaMessageTtlAndPinLockEnabled && {
        messageLifespan: orgSettings?.messageLifespan,
        pinLock: orgSettings?.pinLock,
      }),
    };
  }, [orgSettings, isPersonaMessageTtlAndPinLockEnabled]);

  const defaultUserSettingsRef = useRef(defaultUserSettings);
  defaultUserSettingsRef.current = defaultUserSettings;

  const [errors, setErrors] = useState<
    T extends 'title' ? PersonaTitleFormErrors : PersonaScimFormErrors
  >({} as T extends 'title' ? PersonaTitleFormErrors : PersonaScimFormErrors);

  const loadCommonRequirements = useCallback(async () => {
    setLoading(true);
    try {
      setUserPermissions((selectedPersona?.permissions || {}) as PermissionMap);
      setUserSettings(
        (selectedPersona?.settings || defaultUserSettingsRef.current) as UserSettings
      );
    } finally {
      setLoading(false);
    }
  }, [selectedPersona]);

  useEffect(() => {
    let allowRevert = false;
    if (selectedPersona) {
      const { settings, permissions } = selectedPersona;
      allowRevert = !isEqual(settings, userSettings) || !isEqual(permissions, userPermissions);
    } else {
      const emptyOrAllFalse = (item: Record<string, unknown> | undefined) => {
        return !item || Object.keys(item).every((key) => item[key] === false);
      };
      allowRevert =
        !emptyOrAllFalse(userPermissions) || !isEqual(userSettings, defaultUserSettings);
    }
    setCanRevert(allowRevert);
  }, [selectedPersona, userPermissions, userSettings, defaultUserSettings]);

  useEffect(() => {
    loadCommonRequirements();
  }, [loadCommonRequirements]);

  useEffect(() => {
    if (!template) return;
    setUserPermissions(template.permissions);
    setUserSettings(template.settings as UserSettings);
  }, [template]);

  const revertChanges = () => {
    if (selectedPersona) {
      setUserPermissions(selectedPersona.permissions);
      setUserSettings(selectedPersona.settings as UserSettings);
    } else {
      setUserPermissions({});
      setUserSettings(defaultUserSettings as UserSettings);
    }
    setToastMessageData({
      type: 'SUCCESS',
      message: 'Your changes have been successfully reverted',
    });
  };

  const validateForm = (
    persona: T extends 'title' ? PersonaTitleFormPayload : PersonaScimFormPayload,
    schema: z.ZodSchema
  ) => {
    const errors = getFormErrors<T>(persona, schema);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      const firstError = Object.keys(errors)[0];
      const firstFieldWithError = document.getElementById(firstError);
      firstFieldWithError?.focus();
      window.scrollTo({ top: firstFieldWithError?.offsetTop, behavior: 'smooth' });
      return false;
    }
    return true;
  };

  return {
    canRevert,
    commonFormFields,
    errors,
    loading,
    revertChanges,
    selectedOrganizationId,
    setCanRevert,
    setCommonFormFields,
    setErrors,
    setLoading,
    setTemplate,
    setUserPermissions,
    setUserSettings,
    template,
    userPermissions,
    userSettings,
    validateForm,
  };
};

export default useCommonState;
